import { useEffect, useState } from "react";
import { AtmosphericHero } from "@/components/dashboard/atmospheric-hero";
import { HourlyChart } from "@/components/dashboard/hourly-chart";
import { HourlyTable } from "@/components/dashboard/hourly-table";
import { DailyForecast } from "@/components/dashboard/daily-forecast";
import { AqiGauge } from "@/components/dashboard/aqi-gauge";
import { SunArc } from "@/components/dashboard/sun-arc";
import { WindCompass } from "@/components/dashboard/wind-compass";
import { UvGauge } from "@/components/dashboard/uv-gauge";
import { EnvTrendsPanel } from "@/components/dashboard/env-trends-panel";
import { MinutelyPrecip } from "@/components/dashboard/minutely-precip";
import { UvForecast } from "@/components/dashboard/uv-forecast";
import { Almanac } from "@/components/dashboard/almanac";
import { PressureTrend } from "@/components/dashboard/pressure-trend";
import { WeatherNarrative } from "@/components/dashboard/narrative";
import { WeatherShare } from "@/components/dashboard/weather-share";
import { ActivityPanel } from "@/components/dashboard/activity-panel";
import { StargazingCard } from "@/components/dashboard/stargazing-card";
import { PollenPanel } from "@/components/dashboard/pollen-panel";
import { AuroraPanel } from "@/components/dashboard/aurora-panel";
import { AirQualityPanel } from "@/components/dashboard/air-quality-panel";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GlassPanel } from "@/components/ui/glass-panel";
import { RightInfoPanel } from "@/components/layout/right-info-panel";
import {
  MapPin,
  Droplets,
  Eye,
  CloudRain,
  Cloud,
  Snowflake,
  Gauge,
  RefreshCw,
  TrendingUp,
  Zap,
} from "lucide-react";
import { useLocation } from "@/components/location-provider";
import { useUnit } from "@/components/unit-provider";

export default function DashboardPage() {
  const [weather, setWeather] = useState<Record<string, unknown> | null>(null);
  const [aqi, setAqi] = useState<Record<string, unknown> | null>(null);
  const [aqiForecast, setAqiForecast] = useState<any>(null);
  const [uv, setUv] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dataAge, setDataAge] = useState<string>("");
  const { current: location } = useLocation();
  const { display } = useUnit();

  useEffect(() => {
    setLoading(true);
    fetch(`/api/weather?lat=${location.lat}&lon=${location.lon}`)
      .then((r) => r.json())
      .then((data) => {
        setWeather(data.weather);
        setAqi(data.aqi);
        setAqiForecast(data.aqiForecast);
        setUv(data.uv);
        if (data.weather?.current?.dt) {
          setDataAge(new Date(data.weather.current.dt * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
        }
      })
      .catch(() => setWeather(null))
      .finally(() => setLoading(false));
  }, [location.lat, location.lon]);

  if (loading) {
    return (
      <div className="space-y-5 animate-slide-up">
        <Skeleton className="h-64 rounded-2xl" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-2xl" />
          ))}
        </div>
        <div className="grid gap-4 lg:grid-cols-4">
          <Skeleton className="h-80 rounded-2xl lg:col-span-2" />
          <Skeleton className="h-80 rounded-2xl" />
          <Skeleton className="h-80 rounded-2xl" />
        </div>
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }

  if (!weather) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center animate-slide-up">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted/50 mb-4">
          <MapPin className="h-8 w-8 text-muted-foreground/40" />
        </div>
        <h2 className="text-lg font-semibold">Unable to load weather data</h2>
        <p className="text-sm text-muted-foreground mt-1">Check your connection and try again</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary/10 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/15 transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    );
  }

  const current = weather.current as Record<string, unknown>;
  const hourly = weather.hourly as never[];
  const daily = weather.daily as never[];
  const sunrise = current.sunrise as number;
  const sunset = current.sunset as number;
  const nowTs = current.dt as number;
  const dailyToday = (daily as { sunrise: number; sunset: number; moonrise?: number; moonset?: number; moon_phase?: number }[])?.[0];

  const cloudHigh = (current.clouds as number) > 75;
  const visibility = ((current.visibility as number) / 1000).toFixed(1);
  const dewPoint = current.dew_point as number;
  const pressure = current.pressure as number;

  return (
    <div className="flex gap-4">
      <div className="flex-1 min-w-0 space-y-5 animate-slide-up">
        <AtmosphericHero current={current as never} hourly={hourly as never} daily={daily as never} aqi={aqi as never} />

        <WeatherNarrative weather={weather} aqi={aqi} />

        <MinutelyPrecip minutely={(weather as any)?.minutely} />

        <ActivityPanel current={current as never} hourly={hourly as never} aqi={aqi as never} />

        <GlassPanel intensity="low" className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">At a Glance</h3>
            <div className="flex items-center gap-3">
              <WeatherShare
                location={location.name}
                temp={current.temp as number}
                description={(current.weather as any[])?.[0]?.description || ""}
              />
              <span className="text-[10px] text-muted-foreground/50">Data as of {dataAge}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <GlanceItem icon={TrendingUp} label="High Today" value={`${display((daily[0] as { temp: { max: number } })?.temp?.max || 0)}`} />
            <GlanceItem icon={TrendingUp} label="Low Today" value={`${display((daily[0] as { temp: { min: number } })?.temp?.min || 0)}`} />
            <GlanceItem icon={Zap} label="Wind" value={`${current.wind_speed} m/s`} sub={`${current.wind_deg}°`} />
            <GlanceItem icon={Droplets} label="Rain Chance" value={`${Math.round((daily[0] as { pop?: number })?.pop || 0) * 100}%`} />
          </div>
        </GlassPanel>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 stagger-1">
          <WindCompass speed={current.wind_speed as number} deg={current.wind_deg as number} gust={current.wind_gust as number | undefined} />
          <UvGauge uvi={current.uvi as number} />
          <SunArc sunrise={sunrise} sunset={sunset} current={nowTs} />
          <AqiGauge aqi={aqi as never} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Metric label="Humidity" value={`${current.humidity}%`} sub={`Dew ${display(dewPoint)}`} icon={Droplets} />
          <Metric label="Visibility" value={`${visibility} km`} sub={Number(visibility) > 10 ? "Excellent" : "Reduced"} icon={Eye} />
          <Metric label="Cloud Cover" value={`${current.clouds}%`} sub={cloudHigh ? "Overcast" : "Partly cloudy"} icon={Cloud} />
          <Metric label="Dew Point" value={display(dewPoint)} sub={dewPoint > 18 ? "Muggy" : dewPoint > 13 ? "Comfortable" : "Dry"} icon={CloudRain} />
          <Metric label="Pressure" value={`${pressure}`} sub="hPa" icon={Gauge} />
          <Metric label="Snow" value={(current.snow as { "1h"?: number })?.["1h"] ? `${(current.snow as { "1h"?: number })["1h"]} mm` : "—"} sub="Last hour" icon={Snowflake} />
        </div>

        <div className="grid gap-4 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <HourlyChart hourly={hourly} />
          </div>
          <Almanac sunrise={sunrise} sunset={sunset} moonrise={dailyToday?.moonrise} moonset={dailyToday?.moonset} moonPhase={dailyToday?.moon_phase} />
          <PressureTrend hourly={hourly} currentPressure={pressure} />
        </div>

        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <StargazingCard daily={daily as never} hourly={hourly as never} current={current as never} />
          <AuroraPanel />
          <PollenPanel />
        </div>

        <AirQualityPanel lat={location.lat} lon={location.lon} />

        <HourlyTable hourly={hourly} />

        <DailyForecast daily={daily} />

        <UvForecast uv={uv} />

        <EnvTrendsPanel aqiForecast={aqiForecast} hourly={hourly} />
      </div>

      <RightInfoPanel weather={weather} aqi={aqi} />
    </div>
  );
}

function GlanceItem({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/5 flex-shrink-0">
        <Icon className="h-3.5 w-3.5 text-primary/70" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-sm font-bold tracking-tight">{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon?: typeof Droplets;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <GlassPanel intensity="low" hover className="p-3.5 group/metric">
      <div className="flex items-center gap-2 text-muted-foreground">
        {Icon && (
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/5 group-hover/metric:bg-primary/10 transition-colors">
            <Icon className="h-3 w-3 text-primary/70" />
          </span>
        )}
        <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className="mt-2 text-lg font-bold font-mono tracking-tight">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </GlassPanel>
  );
}